import clsx from 'clsx';
import * as React from 'react';

import { Alert, Button, LoadingIndicator } from '@gitbook/spine-react';
import { sanitizeError } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFACodeInput } from './MFACodeInput';

/**
 * Form to verify and submit a multi-factor authentication code.
 */
export function MFAVerificationForm(props: {
    /**
     * Callback to verify the code.
     * If none is provided,
     */
    submitCode: (code: string) => Promise<void>;

    /**
     * Label for the submit button.
     */
    submitLabel?: string;

    /**
     * Callback when the verification is completed.
     */
    onDone?: () => void;

    /**
     * Left-aligned and centered variant. Used
     */
    align?: 'center' | 'left';
}) {
    const { align = 'center', submitCode, submitLabel = 'Continue', onDone } = props;

    const [loading, wrapLoading] = useLoadingState();
    const [code, setCode] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);

    const isValid = code.length === 6;

    const onSubmit = () => {
        wrapLoading(async () => {
            try {
                if (code.length !== 6) {
                    setError('The code must be 6 characters long');
                    return;
                }
                setError(null);
                await submitCode(code);

                onDone?.();
            } catch (error) {
                if (error.code === 'auth/invalid-verification-code') {
                    setError('The code is invalid. Please try again.');
                    return;
                }
                setError(sanitizeError(error));
            }
        });
    };

    return (
        <div className={clsx('flex flex-col gap-4', align === 'center' && 'text-center')}>
            <p className={clsx(align === 'center' && 'text-center')}>
                To continue, please enter the 6-digit verification code generated by your
                authenticator app.
            </p>
            {error ? <Alert kind="danger" details={error} /> : null}
            <div className="flex">
                <MFACodeInput
                    disabled={loading}
                    value={code}
                    onChange={setCode}
                    onSubmit={onSubmit}
                />
            </div>
            <Button
                label={submitLabel}
                size="large"
                leadingIcon={loading ? LoadingIndicator : undefined}
                kind="primary"
                onPress={onSubmit}
                disabled={!isValid || loading}
                className={clsx('mt-4', align === 'center' && 'self-center')}
            />
        </div>
    );
}
